/**
 * Pure function that generates HTML for changelog preview,
 * pixel-perfect match of the novela-client ChangelogModal.
 *
 * Rendered inside a sandboxed <iframe> via srcDoc for CSS isolation.
 */

import {
  CHANGELOG_PREVIEW_CSS,
  PRETENDARD_URL,
} from "./changelog-preview-styles";

// ─── Types ───

export interface ChangelogPreviewCategory {
  name: string;
  color: string;
}

export interface ChangelogPreviewData {
  title: string;
  body: string;
  featuredImageUrl?: string;
  categories: ChangelogPreviewCategory[];
  publishedAt?: string;
}

// ─── Helpers ───

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);

  return date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// ─── Section Builders ───

function buildCategoryBadgesHtml(
  categories: ChangelogPreviewCategory[],
): string {
  if (categories.length === 0) return "";

  return categories
    .map(
      (cat) =>
        `<span class="category-badge" style="background-color: ${escapeHtml(cat.color)}20; color: ${escapeHtml(cat.color)}; border: 1px solid ${escapeHtml(cat.color)}40;">${escapeHtml(cat.name)}</span>`,
    )
    .join("");
}

function buildMetaHtml(data: ChangelogPreviewData): string {
  const dateHtml = data.publishedAt
    ? `<span class="published-date">${escapeHtml(formatDate(data.publishedAt))}</span>`
    : "";

  const badgesHtml = buildCategoryBadgesHtml(data.categories);

  if (!dateHtml && !badgesHtml) return "";

  return `<div class="changelog-meta">${dateHtml}${badgesHtml}</div>`;
}

function buildFeaturedImageHtml(url?: string): string {
  if (!url) return "";

  return `<img class="featured-image" src="${escapeHtml(url)}" alt="" />`;
}

function buildModalHeaderHtml(): string {
  return `
    <div class="modal-header">
      <div class="modal-header-left">
        <span class="modal-header-title">새로운 소식</span>
      </div>
      <button class="modal-close" aria-label="닫기">✕</button>
    </div>`;
}

function buildSlideHtml(data: ChangelogPreviewData): string {
  return `
    <div class="changelog-slide">
      ${buildFeaturedImageHtml(data.featuredImageUrl)}
      <h4 class="changelog-title">${escapeHtml(data.title)}</h4>
      ${buildMetaHtml(data)}
      <div class="changelog-body">${data.body}</div>
    </div>`;
}

// ─── Main Builders ───

export function buildChangelogBody(data: ChangelogPreviewData): string {
  return `
  <div class="modal-backdrop">
    <div class="modal-card">
      ${buildModalHeaderHtml()}
      ${buildSlideHtml(data)}
    </div>
  </div>`;
}

export function buildChangelogPreviewShellHtml(): string {
  return `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <link rel="stylesheet" href="${PRETENDARD_URL}">
  <style>
    ${CHANGELOG_PREVIEW_CSS}
  </style>
</head>
<body></body>
</html>`;
}

export function buildChangelogPreviewHtml(
  data: ChangelogPreviewData,
): string {
  return `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <link rel="stylesheet" href="${PRETENDARD_URL}">
  <style>
    ${CHANGELOG_PREVIEW_CSS}
  </style>
</head>
<body>
  ${buildChangelogBody(data)}
</body>
</html>`;
}
